/**
 * QR payload encode/decode utilities.
 *
 * QR payloads are JSON, base64-encoded, using the compact event-scoped IDs
 * defined by {@link TeamCheckInQRPayload} and {@link PostScoreQRPayload}. The
 * compact single-letter keys and short IDs keep the encoded string small so the
 * resulting QR code stays quick to scan.
 *
 * These helpers run in both Node (backend Lambdas) and the browser (frontend
 * apps), so they avoid Node-only primitives such as `Buffer`. Encoding routes
 * the JSON through UTF-8 (`TextEncoder`) before base64 (`btoa`) so non-ASCII
 * content survives the round-trip; decoding reverses the process.
 *
 * Requirements: 13.5 (team check-in QR encodes the team's event-scoped ID),
 * 20.2 (post-score QR encodes team ID, station, and per-participant scores),
 * 20.3 (post-score QR includes a unix generation timestamp).
 */
// --- Isomorphic base64 <-> UTF-8 helpers -----------------------------------
/** Encode a UTF-8 string to a base64 string (works in Node and the browser). */
function utf8ToBase64(input) {
    const bytes = new TextEncoder().encode(input);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
/** Decode a base64 string back to a UTF-8 string. Throws on malformed base64. */
function base64ToUtf8(input) {
    const binary = atob(input);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
}
// --- Shape validation -------------------------------------------------------
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isTeamCheckInQRPayload(value) {
    if (!isRecord(value))
        return false;
    return typeof value.e === 'string' && typeof value.t === 'string';
}
function isPostScoreQRParticipant(value) {
    if (!isRecord(value))
        return false;
    return (typeof value.id === 'string' &&
        typeof value.h === 'number' &&
        Number.isFinite(value.h) &&
        typeof value.m === 'number' &&
        Number.isFinite(value.m) &&
        typeof value.dns === 'boolean');
}
function isPostScoreQRPayload(value) {
    if (!isRecord(value))
        return false;
    return (typeof value.e === 'string' &&
        typeof value.s === 'number' &&
        Number.isFinite(value.s) &&
        typeof value.t === 'string' &&
        typeof value.ts === 'number' &&
        Number.isFinite(value.ts) &&
        Array.isArray(value.p) &&
        value.p.every(isPostScoreQRParticipant));
}
/**
 * Parse a base64-encoded JSON QR payload and validate its shape with the given
 * type guard. Centralizes the shared error handling for both payload kinds.
 */
function decode(encoded, guard, label) {
    let json;
    try {
        json = base64ToUtf8(encoded);
    }
    catch {
        return { ok: false, error: 'Invalid base64 QR payload' };
    }
    let parsed;
    try {
        parsed = JSON.parse(json);
    }
    catch {
        return { ok: false, error: 'QR payload is not valid JSON' };
    }
    if (!guard(parsed)) {
        return { ok: false, error: `QR payload does not match ${label} shape` };
    }
    return { ok: true, value: parsed };
}
// --- Public API -------------------------------------------------------------
/** Encode a {@link TeamCheckInQRPayload} as a base64 JSON QR string. */
export function encodeTeamCheckInQR(payload) {
    return utf8ToBase64(JSON.stringify(payload));
}
/** Decode and shape-validate a team check-in QR string. */
export function decodeTeamCheckInQR(encoded) {
    return decode(encoded, isTeamCheckInQRPayload, 'TeamCheckInQRPayload');
}
/** Encode a {@link PostScoreQRPayload} as a base64 JSON QR string. */
export function encodePostScoreQR(payload) {
    return utf8ToBase64(JSON.stringify(payload));
}
/** Decode and shape-validate a post-score QR string. */
export function decodePostScoreQR(encoded) {
    return decode(encoded, isPostScoreQRPayload, 'PostScoreQRPayload');
}
//# sourceMappingURL=index.js.map